import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { Link as RouterLink } from 'react-router-dom';
import patient360Data from '../../mocks/patient360Data.json';
import { useAuth } from '../../auth/useAuth';

const trendIconMap = {
  STABLE: TrendingFlatIcon,
  RISING_SLOWLY: TrendingUpIcon,
  FALLING_SLOWLY: TrendingDownIcon,
  RISING_QUICKLY: TrendingUpIcon,
  FALLING_QUICKLY: TrendingDownIcon,
} as const;

const getDisplayPatient = (patientId?: string, displayName?: string) => {
  const normalizedDisplayName = displayName?.trim().toLowerCase();

  return (
    patient360Data.assignedPatients.find((patient) => patient.id === patientId) ??
    patient360Data.assignedPatients.find(
      (patient) => `${patient.firstName} ${patient.lastName}`.trim().toLowerCase() === normalizedDisplayName
    ) ??
    patient360Data.assignedPatients[0]
  );
};

export const CgmSnapshotWidget = () => {
  const { user } = useAuth();
  const patient = getDisplayPatient(user?.id, user?.displayName);
  const patientDetails = patient360Data.patientDetails[
    patient.id as keyof typeof patient360Data.patientDetails
  ];
  const cgmData = patientDetails?.cgmData.data;
  const recentReading = cgmData?.recentReadings[0];
  const TrendIcon = recentReading ? trendIconMap[recentReading.trend as keyof typeof trendIconMap] ?? TrendingFlatIcon : TrendingFlatIcon;

  if (!cgmData) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MonitorHeartIcon color="primary" />
          CGM Snapshot
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          No CGM data is available yet. Once your device is connected, your latest readings will show here.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, height: '100%' }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MonitorHeartIcon color="primary" />
            CGM Snapshot
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Current device status and recent glucose trends.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label={cgmData.sensorStatus} color={cgmData.sensorStatus === 'Active' ? 'success' : 'warning'} size="small" />
          <Chip label={cgmData.deviceType} variant="outlined" size="small" />
          <Chip label={`Data ${cgmData.dataAvailability}`} variant="outlined" size="small" />
        </Stack>

        {recentReading && (
          <Box
            sx={{
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              p: 2,
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Latest reading
                </Typography>
                <Typography variant="h4">
                  {recentReading.value}
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                    mg/dL
                  </Typography>
                </Typography>
              </Box>
              <TrendIcon color="primary" />
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Updated {new Date(recentReading.timestamp).toLocaleTimeString()} · Last sync {new Date(patientDetails.cgmData.lastUpdated).toLocaleString()}
            </Typography>
          </Box>
        )}

        <Stack direction="row" spacing={3}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Time in range
            </Typography>
            <Typography variant="h6">{cgmData.timeInRange.target}%</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Avg glucose
            </Typography>
            <Typography variant="h6">{cgmData.averageGlucose.value} mg/dL</Typography>
          </Box>
        </Stack>

        <Box>
          <Button component={RouterLink} to="/patient/progress" startIcon={<ShowChartIcon />} variant="outlined">
            View Trends
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
};
